import { Button, Icon, LockHollowSVG } from "@newsela/angelou";

import ContentLevel from "@/types/models/ContentLevel.interface";
import ActionsDropdown from "@/view/ActionsDropdown";
import filterLevelsByLanguage from "@/view/utils/filterLevelsByLanguage";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import renderLevels from "./renderLevels";
import {
  buttonCss,
  dropdownCss,
  disabledButtonCss,
} from "./LevelSelector.style";

interface LevelSelectorInterface {
  contentId: string;
  sortedLevels: ContentLevel[];
  currentLevel: ContentLevel;
  setCurrentLevel: (level: ContentLevel) => ContentLevel;
  isLevelLocked: boolean;
  isLanguageSelectorVisible: boolean;
}

export const LevelSelector = ({
  contentId,
  sortedLevels,
  currentLevel,
  setCurrentLevel,
  isLevelLocked,
  isLanguageSelectorVisible,
}: LevelSelectorInterface) => {
  const levels = filterLevelsByLanguage(sortedLevels, currentLevel?.language);
  const maxLevel = levels[0];
  const buttonText =
    currentLevel?.lexile !== maxLevel?.lexile
      ? currentLevel?.lexile
      : "MAX" || "";

  const { isStudentPreviewOn, isAssignmentLevelLockedForStudentPreview } =
    useStudentPreviewContext();
  const shouldLockLevelForStudentPreview =
    isAssignmentLevelLockedForStudentPreview && isStudentPreviewOn;

  const eventProperties = {
    actionName: "Read",
    actionPrefix: "Initiate",
    objectType: "Content",
    streamProperties: {
      content_type: "Article",
      description: "Toggle content level list.",
      content_id: currentLevel?.id,
    },
  };

  const DisabledButton = () => (
    <Button
      ariaProps={{ "aria-label": `${buttonText} level locked` }}
      __cssFor={{
        root: [buttonCss(isLanguageSelectorVisible), disabledButtonCss],
      }}
      disabled
      flavor={Button.flavors.muted}
    >
      {isOnlyOneLevel ? "Original" : currentLevel?.lexile}
      <Icon size={Icon.sizes.sm} SvgComponent={LockHollowSVG} />
    </Button>
  );

  const isOnlyOneLevel = sortedLevels?.length === 1;

  if (isLevelLocked || shouldLockLevelForStudentPreview || isOnlyOneLevel) {
    return <DisabledButton />;
  }

  return (
    <ActionsDropdown
      ariaProps={{
        "aria-label": `${buttonText} reading level, select to change level`,
      }}
      dataProps={{ "data-qa-selector": `current level: ${currentLevel?.id}` }}
      buttonCss={buttonCss(isLanguageSelectorVisible)}
      buttonFlavor={Button.flavors.muted}
      buttonText={buttonText || ""}
      dropdownCss={dropdownCss}
      eventProperties={{
        ...eventProperties,
      }}
      id={`level-selector-${contentId}`}
      renderDropdownItems={(closeDropdown) => {
        return renderLevels({
          currentLevel,
          closeDropdown,
          eventProperties,
          levels,
          setCurrentLevel,
        });
      }}
    />
  );
};

export default LevelSelector;
